import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { CLIENT_BACKLOG_TYPES, type TicketDetail } from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalHealthBanner } from '../components/PortalHealthBanner.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';
import { Select } from '../components/Select.tsx';
import { Textarea } from '../components/Textarea.tsx';

interface PortalContact {
  id: string;
  organizationId: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

type RequestType = 'escalation' | 'project' | 'quote' | 'administrative';

type AdminKind = 'nda_copy' | 'contract_copy' | 'poc_change' | 'hours_change';

const ADMIN_KIND_LABELS: Record<AdminKind, string> = {
  nda_copy: 'Copy of NDA',
  contract_copy: 'Copy of contract',
  poc_change: 'Change of point of contact',
  hours_change: 'Change of monthly block hours',
};

const URGENCY_OPTIONS: Array<{ value: 'low' | 'normal' | 'high' | 'urgent'; label: string }> = [
  { value: 'low', label: 'Low' },
  { value: 'normal', label: 'Normal' },
  { value: 'high', label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

export function PortalTicketCreatePage(): React.ReactElement {
  const navigate = useNavigate();
  const [contact, setContact] = useState<PortalContact | null>(null);
  const [type, setType] = useState<RequestType>('escalation');
  const [subject, setSubject] = useState('');
  const [clientTicketNumber, setClientTicketNumber] = useState('');
  const [clientName, setClientName] = useState('');
  const [urgency, setUrgency] = useState<'low' | 'normal' | 'high' | 'urgent'>('normal');
  const [passwordAccess, setPasswordAccess] = useState(false);
  const [toBacklog, setToBacklog] = useState(false);
  const backlogOffered = CLIENT_BACKLOG_TYPES.includes(type);

  // ticket / project description
  const [body, setBody] = useState('');
  const [stepsTaken, setStepsTaken] = useState('');
  const [testMachine, setTestMachine] = useState('');
  // quote request
  const [timeFrame, setTimeFrame] = useState('');

  // admin
  const [adminKind, setAdminKind] = useState<AdminKind>('nda_copy');
  const [newPocName, setNewPocName] = useState('');
  const [newPocEmail, setNewPocEmail] = useState('');
  const [newMonthlyHours, setNewMonthlyHours] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    apiFetch<{ contact: PortalContact | null }>('/api/portal/auth/me')
      .then((r) => {
        if (!r.contact) {
          navigate('/login', { replace: true, state: { from: '/portal/tickets/new' } });
          return;
        }
        setContact(r.contact);
        if (!clientName && r.contact.name) setClientName(r.contact.name);
      })
      .catch(() => {
        navigate('/login', { replace: true, state: { from: '/portal/tickets/new' } });
      });
  }, [navigate]);

  // Auto-suggest a subject when admin subtype changes (only if user hasn't typed one).
  useEffect(() => {
    if (type !== 'administrative') return;
    if (subject.trim() && !Object.values(ADMIN_KIND_LABELS).includes(subject.trim())) return;
    setSubject(ADMIN_KIND_LABELS[adminKind]);
  }, [type, adminKind]);

  function validateBeforeSubmit(): string | null {
    if (!subject.trim()) return 'Subject is required.';
    if (!clientName.trim()) return 'Client name is required.';
    if (type === 'escalation') {
      if (!body.trim()) return 'Description is required.';
      if (!stepsTaken.trim()) return 'Steps taken is required.';
    }
    if (type === 'quote' && !body.trim()) return 'Tell us what you would like quoted.';
    if (type === 'administrative') {
      if (adminKind === 'poc_change') {
        if (!newPocName.trim()) return 'New POC name is required.';
        if (!newPocEmail.trim()) return 'New POC email is required.';
      }
      if (adminKind === 'hours_change') {
        const n = Number(newMonthlyHours);
        if (!newMonthlyHours.trim() || !Number.isFinite(n)) {
          return 'New monthly hours is required.';
        }
        if (n <= 0) {
          return "To cancel your subscription, contact us — cancellations require 3 months' notice.";
        }
        if (n % 5 !== 0) {
          return 'Monthly hours must be a multiple of 5.';
        }
      }
    }
    return null;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const v = validateBeforeSubmit();
    if (v) {
      setError(v);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.set('type', type);
      fd.set('subject', subject.trim());
      if (clientTicketNumber.trim()) fd.set('client_ticket_number', clientTicketNumber.trim());
      fd.set('client_name', clientName.trim());
      fd.set('urgency', urgency);
      if (backlogOffered && toBacklog) fd.set('backlog', 'true');
      if (type === 'escalation') {
        fd.set('bodyHtml', body);
        fd.set('steps_taken', stepsTaken.trim());
        if (testMachine.trim()) fd.set('test_machine', testMachine.trim());
        fd.set('password_access', passwordAccess ? 'true' : 'false');
      } else if (type === 'project') {
        if (body.trim()) fd.set('bodyHtml', body);
        fd.set('password_access', passwordAccess ? 'true' : 'false');
      } else if (type === 'quote') {
        fd.set('bodyHtml', body);
        if (timeFrame.trim()) fd.set('time_frame', timeFrame.trim());
      } else {
        fd.set('request_kind', adminKind);
        if (adminKind === 'poc_change') {
          fd.set('new_poc_name', newPocName.trim());
          fd.set('new_poc_email', newPocEmail.trim());
        }
        if (adminKind === 'hours_change') {
          fd.set('new_monthly_hours', String(Number(newMonthlyHours)));
        }
        if (adminNotes.trim()) fd.set('notes', adminNotes.trim());
      }
      for (const f of files) fd.append('files', f, f.name);

      const res = (await apiFetch('/api/portal/tickets', { method: 'POST', body: fd })) as {
        ticket: TicketDetail;
      };
      navigate(`/portal/tickets/${res.ticket.id}`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to submit request.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-white">
      <PortalHealthBanner />
      <PortalNavBar contact={contact} active="tickets" />
      <main className="mx-auto max-w-3xl space-y-6 px-6 py-10 sm:px-8">
        <div>
          <Link to="/portal/tickets" className="text-sm text-[var(--color-muted)] hover:underline">
            ← My tickets
          </Link>
          <h1 className="mt-1 font-display text-2xl font-bold text-[var(--color-header)]">
            Submit a request
          </h1>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Pick a request type. We'll route it to the right person on our team.
          </p>
        </div>

        <Card>
          <div className="text-xs font-semibold uppercase tracking-wide text-[var(--color-muted)]">
            Type of request
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <TypeChoice
              checked={type === 'escalation'}
              onClick={() => setType('escalation')}
              label="Ticket"
              sub="Something is broken, slow, or needs help right now."
            />
            <TypeChoice
              checked={type === 'project'}
              onClick={() => setType('project')}
              label="Project"
              sub="Larger piece of work — migration, rollout, planned change."
            />
            <TypeChoice
              checked={type === 'quote'}
              onClick={() => setType('quote')}
              label="Quote request"
              sub="Get a price for new work before you commit to it."
            />
            <TypeChoice
              checked={type === 'administrative'}
              onClick={() => setType('administrative')}
              label="Administrative"
              sub="Account-related: NDA, contract, POC, or hours change."
            />
          </div>
        </Card>

        <Card>
          <form onSubmit={submit} className="space-y-4">
            <FormField
              label="Subject"
              name="subject"
              required
              maxLength={500}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={
                type === 'escalation'
                  ? 'e.g. Email not syncing on Outlook'
                  : type === 'project'
                    ? 'e.g. Migrate file shares to SharePoint'
                    : type === 'quote'
                      ? 'e.g. Quote for replacing our two file servers'
                      : ADMIN_KIND_LABELS[adminKind]
              }
            />

            <FormField
              label="Your ticket number (optional)"
              name="client_ticket_number"
              maxLength={50}
              value={clientTicketNumber}
              onChange={(e) => setClientTicketNumber(e.target.value)}
              placeholder="e.g. TS10058"
              hint="If you track this in your own system, add your reference. We'll show it on the ticket and in email replies."
            />

            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label="Client name"
                name="client_name"
                required
                maxLength={200}
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                hint={
                  type === 'administrative' || type === 'quote'
                    ? 'Your name (the requester).'
                    : 'The person experiencing the issue.'
                }
              />
              <Select
                label="Urgency"
                value={urgency}
                onChange={(e) =>
                  setUrgency(e.target.value as 'low' | 'normal' | 'high' | 'urgent')
                }
              >
                {URGENCY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
            </div>

            {type === 'escalation' && (
              <>
                <Textarea
                  label="Description — what's happening?"
                  name="body"
                  rows={6}
                  required
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  hint="Include any error messages, when it started, and how often it happens."
                />
                <Textarea
                  label="Steps taken"
                  name="steps_taken"
                  rows={3}
                  required
                  value={stepsTaken}
                  onChange={(e) => setStepsTaken(e.target.value)}
                  hint="What's already been tried? Even 'nothing yet' is fine."
                />
                <FormField
                  label="Test machine name (optional)"
                  name="test_machine"
                  maxLength={200}
                  value={testMachine}
                  onChange={(e) => setTestMachine(e.target.value)}
                  hint="If we should look at a specific computer, what's its hostname?"
                />
              </>
            )}

            {type === 'project' && (
              <Textarea
                label="What do you need (optional)"
                name="body"
                rows={5}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                hint="A short description helps us scope. We'll follow up to plan details."
              />
            )}

            {type === 'quote' && (
              <>
                <Textarea
                  label="What would you like quoted?"
                  name="body"
                  rows={6}
                  required
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  hint="Describe the work: what you have today, what you want it to become, and anything you already know you need. We'll ask about the rest."
                />
                <FormField
                  label="When do you need it? (optional)"
                  name="time_frame"
                  maxLength={1000}
                  value={timeFrame}
                  onChange={(e) => setTimeFrame(e.target.value)}
                  placeholder="e.g. Before our lease ends in March"
                />
                <p className="text-xs text-[var(--color-muted)]">
                  We'll scope it and send you a quote to review and sign online. Nothing is charged until you accept it.
                </p>
              </>
            )}

            {(type === 'escalation' || type === 'project') && (
              <label className="flex items-start gap-3 text-sm">
                <input
                  type="checkbox"
                  checked={passwordAccess}
                  onChange={(e) => setPasswordAccess(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[var(--color-border)]"
                />
                <span>
                  <span className="font-medium text-[var(--color-header)]">Password access provided</span>
                  <span className="block text-xs text-[var(--color-muted)]">
                    Check this if we already have the credentials needed to investigate.
                  </span>
                </span>
              </label>
            )}

            {type === 'administrative' && (
              <>
                <Select
                  label="Request"
                  value={adminKind}
                  onChange={(e) => setAdminKind(e.target.value as AdminKind)}
                >
                  {(Object.entries(ADMIN_KIND_LABELS) as Array<[AdminKind, string]>).map(([v, l]) => (
                    <option key={v} value={v}>
                      {l}
                    </option>
                  ))}
                </Select>

                {adminKind === 'poc_change' && (
                  <div className="grid gap-4 sm:grid-cols-2">
                    <FormField
                      label="New POC — name"
                      name="new_poc_name"
                      required
                      maxLength={200}
                      value={newPocName}
                      onChange={(e) => setNewPocName(e.target.value)}
                    />
                    <FormField
                      label="New POC — email"
                      name="new_poc_email"
                      type="email"
                      required
                      maxLength={200}
                      value={newPocEmail}
                      onChange={(e) => setNewPocEmail(e.target.value)}
                    />
                  </div>
                )}

                {adminKind === 'hours_change' && (
                  <div>
                    <FormField
                      label="New monthly block hours"
                      name="new_monthly_hours"
                      type="number"
                      required
                      min={5}
                      step={5}
                      value={newMonthlyHours}
                      onChange={(e) => setNewMonthlyHours(e.target.value)}
                      hint="Must be a positive multiple of 5. Cancellations require 3 months' notice — contact us instead."
                    />
                  </div>
                )}

                <Textarea
                  label="Notes (optional)"
                  name="notes"
                  rows={3}
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  hint="Anything else we should know."
                />
              </>
            )}

            {type !== 'administrative' && (
              <div>
                <label className="block text-sm font-medium text-[var(--color-header)]">
                  Attachments (optional)
                </label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                  className="mt-1 block w-full text-sm text-[var(--color-body)] file:mr-3 file:rounded-[var(--radius-button)] file:border-0 file:bg-[var(--color-t3-blue)] file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-[var(--color-t3-blue)]/90"
                />
                {files.length > 0 && (
                  <ul className="mt-2 text-xs text-[var(--color-muted)]">
                    {files.map((f, i) => (
                      <li key={i}>📎 {f.name}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {backlogOffered && (
              <label className="flex items-start gap-3 rounded-[var(--radius-button)] border border-violet-200 bg-violet-50 p-3 text-sm">
                <input
                  type="checkbox"
                  checked={toBacklog}
                  onChange={(e) => setToBacklog(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded border-[var(--color-border)]"
                />
                <span>
                  <span className="font-medium text-[var(--color-header)]">Add to backlog</span>
                  <span className="block text-xs text-[var(--color-muted)]">
                    Not needed yet. We'll file it in your backlog and won't start work until you
                    move it out of the backlog from the ticket page.
                  </span>
                </span>
              </label>
            )}

            {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

            <Button type="submit" disabled={busy}>
              {busy ? 'Submitting…' : backlogOffered && toBacklog ? 'Add to backlog' : 'Submit request'}
            </Button>
          </form>
        </Card>
      </main>
      <PortalFooter />
    </div>
  );
}

function TypeChoice({
  checked,
  onClick,
  label,
  sub,
}: {
  checked: boolean;
  onClick: () => void;
  label: string;
  sub: string;
}): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'rounded-[var(--radius-card)] border p-4 text-left transition ' +
        (checked
          ? 'border-[var(--color-t3-blue)] bg-[var(--color-t3-blue)]/5 ring-2 ring-[var(--color-t3-blue)]/30'
          : 'border-[var(--color-border)] bg-white hover:border-[var(--color-t3-blue)]/40')
      }
    >
      <div className="font-semibold text-[var(--color-header)]">{label}</div>
      <div className="mt-1 text-xs text-[var(--color-muted)]">{sub}</div>
    </button>
  );
}
